/**
 * `SkillAssessmentWizard` — schermata di valutazione **skill per skill** con i
 * form di autovalutazione **sempre attivi**.
 *
 * È il pendant "per skill" di `AssessmentWizard` (che valuta competenza per
 * competenza). A differenza della pagina "Le mie skill" (`SkillList`), dove la
 * valutazione al volo richiede di aprire l'editor riga per riga (icona penna),
 * qui ogni skill mostra da subito gli input di autovalutazione (Livello,
 * Esperienza, Propensione), pronti all'uso.
 *
 * Comportamento:
 *  - presenta le skill in un'unica lista, con due filtri affiancati:
 *     1. un **filtro di ambito** (`scopeFilter`): "Le mie skill" / "Non
 *        valutate" / "Tutte le skill", più un optgroup "Per business unit" (una
 *        voce per reparto) e un optgroup "Per professione" (una voce per Job).
 *        Reparti e professioni non hanno skill dirette: l'insieme delle skill si
 *        deriva risalendo le competenze (reparto → competenze con quel
 *        `departmentId` → `skillIds`; Job → `competenceIds` → `skillIds`);
 *     2. un **filtro per tipologia** di skill (`typeFilter`): tutte, oppure una
 *        delle tipologie e-CF (`knowledge`/`skill`/`technology`), più "Senza
 *        tipo" per le skill legacy prive del campo.
 *    Una ricerca testuale sul nome skill opera all'interno della selezione.
 *  - ogni riga espone gli stessi input del wizard competenze: il **Livello_Self**
 *    è l'unico campo obbligatorio (Req. 8.6); Esperienza e Propensione sono
 *    facoltativi e informativi (Req. 8.1, 8.2);
 *  - il salvataggio è **implicito**, al cambio di un campo (come nel wizard):
 *    non appena il Livello è scelto, la riga viene salvata via
 *    `PUT /api/assessments/me/:skillId/self`; l'invalidazione di
 *    `['assessments','me']` riflette lo stato corrente.
 *
 * Lo stato server (assessment, catalogo skill/competenze, reparti, professioni)
 * è gestito con TanStack Query. Le bozze locali (Livello/Esperienza/Propensione)
 * consentono di comporre la scelta prima del salvataggio.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AssessmentView,
  CompetenceDto,
  DepartmentDto,
  ExperienceRange,
  JobDto,
  Livello,
  SetScoreRequest,
  SkillDto,
  SkillType,
  SuggestedSkillsDto,
} from '../shared/dto';
import { SKILL_TYPE_LABELS, SKILL_TYPE_VALUES } from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { useAuth } from '../auth/AuthProvider';
import { ExperienceRangeSelect } from '../components/ExperienceRangeSelect';
import { LivelloGuide } from '../components/LivelloGuide';
import { LivelloSelect } from '../components/LivelloSelect';
import { PropensionSelect } from '../components/PropensionSelect';
import {
  Alert,
  Button,
  Card,
  Field,
  PageHeader,
  TextInput,
} from '../components/ui';

/** Query keys locali della pagina. */
const QK = {
  competences: ['competences'] as const,
  skills: ['skills'] as const,
  departments: ['departments'] as const,
  jobs: ['jobs'] as const,
  assessmentsMe: ['assessments', 'me'] as const,
  suggestedSkills: ['dashboard', 'suggested-skills'] as const,
};

/**
 * Prefissi usati per i valori del filtro di ambito che puntano a un reparto o a
 * una professione specifici. Le voci generali (`mine`/`unrated`/`all`) non hanno
 * prefisso.
 */
const DEPT_PREFIX = 'dept:';
const JOB_PREFIX = 'job:';

/**
 * Valore del filtro di ambito (primo select):
 * - `'mine'`: solo le skill di cui l'utente ha un assessment;
 * - `'unrated'`: solo le skill senza alcuna fonte di Livello (Self, AI, reviewer,
 *   manager tutti assenti), ovvero ancora da valutare;
 * - `'all'`: tutte le skill del catalogo;
 * - `dept:<id>`: le skill delle competenze del reparto scelto (business unit);
 * - `job:<id>`: le skill delle competenze richieste dalla professione scelta.
 */
type ScopeFilter = 'mine' | 'unrated' | 'all' | string;

/**
 * Valore del filtro per tipologia (secondo select):
 * - `'all'`: qualsiasi tipologia;
 * - `SkillType`: solo le skill di quella tipologia (`knowledge`/`skill`/`technology`);
 * - `'none'`: solo le skill prive di tipologia (documenti legacy).
 */
type TypeFilter = 'all' | SkillType | 'none';

/**
 * Schermata di valutazione skill con form sempre attivi.
 */
export default function SkillAssessmentWizard() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { me } = useAuth();

  // Step di scelta iniziale: finché `started` è `false` si mostra il selettore
  // con i tre "tastoni" (per business unit / non valutate / tutte); scegliendone
  // uno si preimposta il filtro di ambito e si entra nella lista.
  const [started, setStarted] = useState(false);

  // Assessment correnti dell'utente: pre‑popolano gli input e restano sempre
  // modificabili (overwrite, nessuno storico).
  const assessmentsQuery = useQuery<AssessmentView[]>({
    queryKey: QK.assessmentsMe,
    queryFn: () => api.get<AssessmentView[]>('/api/assessments/me'),
  });

  // Catalogo competenze: è l'anello che collega le skill a reparti (via
  // `departmentId`) e a professioni (via `JobDto.competenceIds`).
  const competencesQuery = useQuery<CompetenceDto[]>({
    queryKey: QK.competences,
    queryFn: () => api.get<CompetenceDto[]>('/api/competences'),
  });

  // Catalogo completo delle skill: consente di mostrare anche le skill non
  // ancora valutate e fornisce la tipologia (`type`) per il secondo filtro.
  const skillsQuery = useQuery<SkillDto[]>({
    queryKey: QK.skills,
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
  });

  // Reparti (business unit): id → nome per l'optgroup "Per business unit".
  const departmentsQuery = useQuery<DepartmentDto[]>({
    queryKey: QK.departments,
    queryFn: () => api.get<DepartmentDto[]>('/api/departments'),
  });

  // Professioni (job): forniscono le competenze richieste per l'optgroup
  // "Per professione".
  const jobsQuery = useQuery<JobDto[]>({
    queryKey: QK.jobs,
    queryFn: () => api.get<JobDto[]>('/api/jobs'),
  });

  // Skill suggerite: fino a 20 skill, non ancora valutate, ordinate per
  // popolarità. Con un reparto assegnato sono le skill della propria Business
  // Unit; senza reparto, il backend ripiega sull'intero catalogo (popolarità
  // sull'intero organico). La tabella si mostra quando ci sono suggerimenti.
  const suggestedSkillsQuery = useQuery<SuggestedSkillsDto>({
    queryKey: QK.suggestedSkills,
    queryFn: () => api.get<SuggestedSkillsDto>('/api/dashboard/suggested-skills'),
  });

  // Testo di ricerca sul nome skill.
  const [search, setSearch] = useState('');
  // Filtro di ambito. Default `'unrated'` (Non valutate): in questa pagina
  // l'obiettivo è completare le skill ancora prive di voto.
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('unrated');
  // Filtro per tipologia. Default `'all'` (qualsiasi tipologia).
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  // Skill "pinnate": lo snapshot delle skill già presenti nella lista corrente.
  // Con il filtro "Non valutate" (`unrated`) — o con qualsiasi ambito che
  // dipenda dallo stato di valutazione — salvare un Livello rende la skill
  // "valutata" e, senza questo accorgimento, la riga sparirebbe subito dopo la
  // selezione del primo select (mentre l'utente sta ancora compilando
  // Esperienza/Propensione). Le skill pinnate restano visibili anche dopo il
  // salvataggio, finché non si cambia ambito/tipo/ricerca o si rientra nella
  // lista. Il popolamento avviene in un `useEffect` a valle del filtro.
  const [pinnedSkillIds, setPinnedSkillIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  const assessments = useMemo(
    () => assessmentsQuery.data ?? [],
    [assessmentsQuery.data],
  );
  const competences = useMemo(
    () => competencesQuery.data ?? [],
    [competencesQuery.data],
  );
  const skills = useMemo(() => skillsQuery.data ?? [], [skillsQuery.data]);
  const jobs = useMemo(() => jobsQuery.data ?? [], [jobsQuery.data]);

  // Mappa skillId → SkillDto: serve per la tipologia, la descrizione e i flag.
  const skillById = useMemo(() => new Map(skills.map((s) => [s.id, s])), [skills]);

  // Insieme delle skill "possedute" dall'utente (con un assessment registrato).
  const mySkillIds = useMemo(
    () => new Set(assessments.map((a) => a.skillId)),
    [assessments],
  );

  // Righe complete: l'intero catalogo skill con i voti dell'utente quando
  // presenti (altrimenti assenti). Le skill valutate ma non più in catalogo
  // (dato orfano) restano incluse per non perdere i voti dell'utente.
  const rows = useMemo<AssessmentView[]>(() => {
    const assessmentBySkill = new Map(assessments.map((a) => [a.skillId, a]));
    const merged: AssessmentView[] = skills.map((s) => {
      const existing = assessmentBySkill.get(s.id);
      if (existing) return existing;
      return {
        skillId: s.id,
        skillName: s.name,
        selfInterest: null,
        aiLevel: null,
        reviewerLevelRank: null,
        reviewerCount: 0,
        reviewerUniformLevel: null,
        managerLevelRank: null,
        managerCount: 0,
        managerUniformLevel: null,
        livelloSelf: null,
        yearsOfExperience: null,
        score: null,
        certificationCount: 0,
        reviews: [],
      };
    });
    const catalogIds = new Set(skills.map((s) => s.id));
    for (const a of assessments) {
      if (!catalogIds.has(a.skillId)) merged.push(a);
    }
    return merged;
  }, [skills, assessments]);

  // Mappa competenceId → skill collegate. Base per derivare gli insiemi di skill
  // di reparti e professioni (che non hanno skill dirette).
  const skillIdsByCompetence = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const c of competences) map.set(c.id, new Set(c.skillIds));
    return map;
  }, [competences]);

  // Reparti presenti (id → nome), ordinati alfabeticamente, per l'optgroup
  // "Per business unit". Includiamo solo i reparti con almeno una competenza
  // collegata a una skill (le voci vuote non aiutano a filtrare).
  const departmentOptions = useMemo(() => {
    const nameById = new Map(
      (departmentsQuery.data ?? []).map((d) => [d.id, d.name]),
    );
    const withSkills = new Set<string>();
    for (const c of competences) {
      if (c.departmentId !== null && c.skillIds.length > 0) {
        withSkills.add(c.departmentId);
      }
    }
    const collator = new Intl.Collator('it', { sensitivity: 'base' });
    return [...withSkills]
      .filter((id) => nameById.has(id))
      .map((id) => ({ id, name: nameById.get(id) as string }))
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [competences, departmentsQuery.data]);

  // Professioni con almeno una competenza, ordinate alfabeticamente, per
  // l'optgroup "Per professione".
  const jobOptions = useMemo(() => {
    const collator = new Intl.Collator('it', { sensitivity: 'base' });
    return jobs
      .filter((j) => j.competenceIds.length > 0)
      .map((j) => ({ id: j.id, name: j.name }))
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [jobs]);

  // reparto → insieme delle skill (unione delle skill delle sue competenze).
  const skillIdsByDepartment = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const c of competences) {
      if (c.departmentId === null) continue;
      const set = map.get(c.departmentId) ?? new Set<string>();
      for (const skillId of c.skillIds) set.add(skillId);
      map.set(c.departmentId, set);
    }
    return map;
  }, [competences]);

  // professione → insieme delle skill (unione delle skill delle competenze
  // richieste dal Job).
  const skillIdsByJob = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const job of jobs) {
      const set = new Set<string>();
      for (const competenceId of job.competenceIds) {
        const skillsOfComp = skillIdsByCompetence.get(competenceId);
        if (skillsOfComp) for (const skillId of skillsOfComp) set.add(skillId);
      }
      map.set(job.id, set);
    }
    return map;
  }, [jobs, skillIdsByCompetence]);

  // Righe filtrate: prima l'ambito, poi la tipologia, infine la ricerca testuale.
  // Ordinate per nome skill (case‑insensitive, locale `it`).
  const filteredRows = useMemo(() => {
    let result = rows;

    // 1) Ambito.
    if (scopeFilter === 'mine') {
      // Le skill pinnate restano visibili anche se, modificandone i voti, non
      // rientrassero più nel criterio: evita che una riga sparisca sotto le mani.
      result = result.filter(
        (r) => mySkillIds.has(r.skillId) || pinnedSkillIds.has(r.skillId),
      );
    } else if (scopeFilter === 'unrated') {
      // "Non valutate": una skill esce dal criterio appena riceve un Livello.
      // Le skill pinnate (già mostrate in questa sessione di lista) restano
      // visibili così l'utente può completare Esperienza/Propensione senza che la
      // riga scompaia subito dopo aver scelto il Livello.
      result = result.filter(
        (r) =>
          pinnedSkillIds.has(r.skillId) ||
          (r.livelloSelf === null &&
            r.aiLevel === null &&
            r.reviewerLevelRank === null &&
            r.managerLevelRank === null),
      );
    } else if (scopeFilter.startsWith(DEPT_PREFIX)) {
      const allowed = skillIdsByDepartment.get(scopeFilter.slice(DEPT_PREFIX.length));
      result = allowed ? result.filter((r) => allowed.has(r.skillId)) : [];
    } else if (scopeFilter.startsWith(JOB_PREFIX)) {
      const allowed = skillIdsByJob.get(scopeFilter.slice(JOB_PREFIX.length));
      result = allowed ? result.filter((r) => allowed.has(r.skillId)) : [];
    }
    // `'all'`: nessun filtro di ambito.

    // 2) Tipologia.
    if (typeFilter !== 'all') {
      result = result.filter((r) => {
        const type = skillById.get(r.skillId)?.type;
        return typeFilter === 'none' ? type === undefined : type === typeFilter;
      });
    }

    // 3) Ricerca testuale sul nome.
    const q = search.trim().toLowerCase();
    if (q.length > 0) {
      result = result.filter((r) => r.skillName.toLowerCase().includes(q));
    }

    return [...result].sort((a, b) =>
      a.skillName.localeCompare(b.skillName, 'it', { sensitivity: 'base' }),
    );
  }, [
    rows,
    scopeFilter,
    typeFilter,
    search,
    mySkillIds,
    skillIdsByDepartment,
    skillIdsByJob,
    skillById,
    pinnedSkillIds,
  ]);

  // Pinna le skill attualmente visibili così restano in lista anche dopo un
  // salvataggio che le farebbe uscire dal criterio d'ambito (es. "Non
  // valutate"). Il set è additivo (cresce soltanto) quindi converge: aggiungere
  // gli id già presenti non provoca aggiornamenti né loop. Il reset del pin
  // avviene quando cambiano ambito/tipo/ricerca o si (ri)entra nella lista.
  useEffect(() => {
    setPinnedSkillIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const r of filteredRows) {
        if (!next.has(r.skillId)) {
          next.add(r.skillId);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [filteredRows]);

  // ----- Conteggi per lo step di scelta iniziale (i tre "tastoni") -----

  // Una skill è "valutata" se ha almeno una fonte di Livello (Self/AI/reviewer/
  // manager). È l'inverso del filtro "Non valutate".
  const ratedSkillIds = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      const rated =
        r.livelloSelf !== null ||
        r.aiLevel !== null ||
        r.reviewerLevelRank !== null ||
        r.managerLevelRank !== null;
      if (rated) set.add(r.skillId);
    }
    return set;
  }, [rows]);

  // Business unit dell'utente (o `null` se non assegnata) e relativo insieme di
  // skill. Se manca la BU, il primo tastone è disabilitato.
  const myDepartmentId = me?.departmentId ?? null;
  const myDepartmentSkillIds = useMemo(
    () => (myDepartmentId !== null ? skillIdsByDepartment.get(myDepartmentId) ?? null : null),
    [myDepartmentId, skillIdsByDepartment],
  );
  // Nome della business unit dell'utente, per mostrarlo nella relativa card.
  const myDepartmentName = useMemo(() => {
    if (myDepartmentId === null) return null;
    return (
      (departmentsQuery.data ?? []).find((d) => d.id === myDepartmentId)?.name ?? null
    );
  }, [myDepartmentId, departmentsQuery.data]);

  // Conteggi valutate/totale per ciascuna scelta.
  const counts = useMemo(() => {
    const totalAll = rows.length;
    const ratedAll = rows.reduce(
      (n, r) => n + (ratedSkillIds.has(r.skillId) ? 1 : 0),
      0,
    );

    let totalBu = 0;
    let ratedBu = 0;
    if (myDepartmentSkillIds !== null) {
      for (const r of rows) {
        if (!myDepartmentSkillIds.has(r.skillId)) continue;
        totalBu += 1;
        if (ratedSkillIds.has(r.skillId)) ratedBu += 1;
      }
    }

    return {
      all: { rated: ratedAll, total: totalAll },
      bu: { rated: ratedBu, total: totalBu },
      // "Che mi mancano": le non ancora valutate sul catalogo completo.
      missing: totalAll - ratedAll,
    };
  }, [rows, ratedSkillIds, myDepartmentSkillIds]);

  // Applica la scelta di uno dei tastoni: preimposta il filtro di ambito e entra
  // nella lista. La tipologia resta "tutte" e la ricerca vuota.
  const startWith = (scope: ScopeFilter) => {
    setScopeFilter(scope);
    setTypeFilter('all');
    setSearch('');
    // Nuova sessione di lista: riparti da uno snapshot pulito delle skill pinnate.
    setPinnedSkillIds(new Set<string>());
    setStarted(true);
  };

  // Skill suggerite mappate in `AssessmentView`, così da riusare 1:1 i
  // componenti di autovalutazione (Livello/Esperienza/Propensione) della
  // tabella principale. L'ordinamento per popolarità deciso dal backend viene
  // preservato (nessun riordino per nome qui). Normalmente queste skill non
  // hanno ancora un assessment (sono escluse quelle già valutate), ma se ne
  // esistesse uno lo riusiamo per pre‑popolare gli input.
  const suggestedRows = useMemo<AssessmentView[]>(() => {
    const payload = suggestedSkillsQuery.data?.skills ?? [];
    const assessmentBySkill = new Map(assessments.map((a) => [a.skillId, a]));
    return payload.map((s) => {
      const existing = assessmentBySkill.get(s.skillId);
      if (existing) return existing;
      return {
        skillId: s.skillId,
        skillName: s.name,
        selfInterest: null,
        aiLevel: null,
        reviewerLevelRank: null,
        reviewerCount: 0,
        reviewerUniformLevel: null,
        managerLevelRank: null,
        managerCount: 0,
        managerUniformLevel: null,
        livelloSelf: null,
        yearsOfExperience: null,
        score: null,
        certificationCount: 0,
        reviews: [],
      };
    });
  }, [suggestedSkillsQuery.data, assessments]);

  // ----- Bozze locali di autovalutazione (come nel wizard competenze) -----

  const [livelloDraft, setLivelloDraft] = useState<Record<string, Livello>>({});
  const [interestDraft, setInterestDraft] = useState<Record<string, number>>({});
  const [yearsDraft, setYearsDraft] = useState<Record<string, ExperienceRange>>({});
  // Ref allineati alle bozze: gli handler leggono il valore più recente degli
  // *altri* campi senza dipendere da uno stato "stale" del closure.
  const livelloDraftRef = useRef<Record<string, Livello>>({});
  const interestDraftRef = useRef<Record<string, number>>({});
  const yearsDraftRef = useRef<Record<string, ExperienceRange>>({});

  // Valori server per skill (per pre‑popolare gli input).
  const livelloBySkill = useMemo(() => {
    const map = new Map<string, Livello | null>();
    for (const a of assessments) map.set(a.skillId, a.livelloSelf);
    return map;
  }, [assessments]);
  const interestBySkill = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const a of assessments) map.set(a.skillId, a.selfInterest);
    return map;
  }, [assessments]);
  const yearsBySkill = useMemo(() => {
    const map = new Map<string, ExperienceRange | null>();
    for (const a of assessments) map.set(a.skillId, a.yearsOfExperience);
    return map;
  }, [assessments]);

  // Salvataggio dell'autovalutazione: PUT /api/assessments/me/:skillId/self. Il
  // Livello_Self è l'unico campo obbligatorio (Req. 8.6); Propensione e anni
  // sono opzionali e viaggiano nello stesso PUT.
  const saveSelfScore = useMutation({
    mutationFn: async ({
      skillId,
      livello,
      interest,
      yearsOfExperience,
    }: {
      skillId: string;
      livello: Livello;
      interest: number | null;
      yearsOfExperience: ExperienceRange | null;
    }) => {
      const body: SetScoreRequest = {
        livello,
        interest: interest ?? null,
        yearsOfExperience,
      };
      await api.put<void>(
        `/api/assessments/me/${encodeURIComponent(skillId)}/self`,
        body,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QK.assessmentsMe });
    },
  });

  // Skill attualmente in salvataggio: disabilita solo la riga interessata.
  const savingSkillId = saveSelfScore.isPending
    ? saveSelfScore.variables?.skillId ?? null
    : null;

  // Valore corrente visualizzato: bozza locale se presente, altrimenti il valore
  // server. `null` = mai valutato (Livello) o nessuna preferenza (interesse/anni).
  const currentLivello = (skillId: string): Livello | null =>
    livelloDraft[skillId] ?? livelloBySkill.get(skillId) ?? null;
  const currentInterest = (skillId: string): number | null =>
    interestDraft[skillId] ?? interestBySkill.get(skillId) ?? null;
  const currentYears = (skillId: string): ExperienceRange | null =>
    yearsDraft[skillId] ?? yearsBySkill.get(skillId) ?? null;

  // Varianti "robuste" lette dai ref (valore più recente) usate al salvataggio.
  const latestLivello = (skillId: string): Livello | null =>
    livelloDraftRef.current[skillId] ?? livelloBySkill.get(skillId) ?? null;
  const latestInterest = (skillId: string): number | null =>
    interestDraftRef.current[skillId] ?? interestBySkill.get(skillId) ?? null;
  const latestYears = (skillId: string): ExperienceRange | null =>
    yearsDraftRef.current[skillId] ?? yearsBySkill.get(skillId) ?? null;

  // Salva la riga e azzera le relative bozze al successo, così torna a
  // riflettere i dati server aggiornati.
  const persist = (
    skillId: string,
    livello: Livello,
    interest: number | null,
    yearsOfExperience: ExperienceRange | null,
  ) => {
    saveSelfScore.mutate(
      { skillId, livello, interest, yearsOfExperience },
      {
        onSuccess: () => {
          delete livelloDraftRef.current[skillId];
          delete interestDraftRef.current[skillId];
          delete yearsDraftRef.current[skillId];
          setLivelloDraft(({ [skillId]: _l, ...rest }) => rest);
          setInterestDraft(({ [skillId]: _i, ...rest }) => rest);
          setYearsDraft(({ [skillId]: _y, ...rest }) => rest);
        },
      },
    );
  };

  // Cambio del Livello (obbligatorio): aggiorna la bozza e salva subito con
  // interesse/anni correnti (letti dai ref). Con Livello "Nessuno" (e-0) gli
  // anni non hanno significato: si azzerano e si persiste con `null`.
  const handleLivelloChange = (skillId: string, livello: Livello) => {
    livelloDraftRef.current = { ...livelloDraftRef.current, [skillId]: livello };
    setLivelloDraft((prev) => ({ ...prev, [skillId]: livello }));

    if (livello === 'e-0') {
      delete yearsDraftRef.current[skillId];
      setYearsDraft(({ [skillId]: _y, ...rest }) => rest);
      persist(skillId, livello, latestInterest(skillId), null);
      return;
    }
    persist(skillId, livello, latestInterest(skillId), latestYears(skillId));
  };

  // Cambio della Propensione (opzionale): aggiorna la bozza e ripersiste solo se
  // il Livello è già impostato (il Livello da solo è la condizione di salvataggio).
  const handleInterestChange = (skillId: string, interest: number | null) => {
    if (interest === null) {
      delete interestDraftRef.current[skillId];
      setInterestDraft(({ [skillId]: _i, ...rest }) => rest);
    } else {
      interestDraftRef.current = { ...interestDraftRef.current, [skillId]: interest };
      setInterestDraft((prev) => ({ ...prev, [skillId]: interest }));
    }
    const livello = latestLivello(skillId);
    if (livello !== null) persist(skillId, livello, interest, latestYears(skillId));
  };

  // Cambio degli anni di esperienza (opzionale): come sopra.
  const handleYearsChange = (skillId: string, years: ExperienceRange | null) => {
    if (years === null) {
      delete yearsDraftRef.current[skillId];
      setYearsDraft(({ [skillId]: _y, ...rest }) => rest);
    } else {
      yearsDraftRef.current = { ...yearsDraftRef.current, [skillId]: years };
      setYearsDraft((prev) => ({ ...prev, [skillId]: years }));
    }
    const livello = latestLivello(skillId);
    if (livello !== null) persist(skillId, livello, latestInterest(skillId), years);
  };

  const isLoading =
    assessmentsQuery.isLoading || skillsQuery.isLoading || competencesQuery.isLoading;

  const emptyMessage =
    scopeFilter === 'mine'
      ? 'Non hai ancora skill valutate. Scegli "Tutte le skill" per vedere il catalogo.'
      : scopeFilter === 'unrated'
        ? 'Nessuna skill senza voti: tutte le tue skill hanno almeno un voto.'
        : 'Nessuna skill corrisponde ai filtri o alla ricerca.';

  // Render di una singola riga skill (etichetta + input di autovalutazione),
  // condiviso dalla tabella principale e da quella delle skill suggerite: stesso
  // markup, stessi handler, stesse bozze. `row` è un `AssessmentView`.
  const renderSkillRow = (row: AssessmentView) => {
    const skill = skillById.get(row.skillId);
    // Le skill legacy prive del flag sono trattate come autovalutabili.
    const selfAssessable = skill?.selfAssessable ?? true;
    const isSaving = savingSkillId === row.skillId;
    const livello = currentLivello(row.skillId);
    const interest = currentInterest(row.skillId);
    // Con Livello "Nessuno" (e-0) gli anni non hanno significato: vuoto e disabilitato.
    const yearsValue = livello === 'e-0' ? null : currentYears(row.skillId);
    // Finché manca il Livello (Req. 8.6) il select è bordato di rosso.
    const needsLevel = livello === null;
    const levelClasses = [
      'user-admin__select',
      'skill-list__picker-level',
      needsLevel ? 'skill-list__picker-level--invalid' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <li key={row.skillId} className="assessment-wizard__step">
        <span className="assessment-wizard__skill-label assessment-wizard__skill-label--stacked">
          <span className="assessment-wizard__skill-name assessment-wizard__skill-name--sm">
            {row.skillName}
          </span>
          {skill?.description && (
            <span className="assessment-wizard__skill-desc">
              {skill.description}
            </span>
          )}
        </span>
        {selfAssessable ? (
          <div className="assessment-wizard__inputs">
            <span className={levelClasses} data-empty={livello === null || undefined}>
              <LivelloSelect
                label={`Livello per ${row.skillName}`}
                value={livello}
                disabled={isSaving}
                onChange={(next) => handleLivelloChange(row.skillId, next)}
              />
            </span>
            <span
              className="user-admin__select skill-list__picker-years"
              data-empty={yearsValue === null || undefined}
            >
              <ExperienceRangeSelect
                label={`Anni di esperienza per ${row.skillName}`}
                value={yearsValue}
                disabled={isSaving || livello === 'e-0'}
                onChange={(next) => handleYearsChange(row.skillId, next)}
              />
            </span>
            <span className="assessment-wizard__interest">
              <span
                className="user-admin__select skill-list__picker-propension"
                data-empty={interest === null || undefined}
              >
                <PropensionSelect
                  label={`Propensione per ${row.skillName}`}
                  placeholder="Propensione…"
                  value={interest}
                  disabled={isSaving}
                  onChange={(v) => handleInterestChange(row.skillId, v)}
                />
              </span>
            </span>
          </div>
        ) : (
          <span className="assessment-wizard__not-self-assessable">
            Questa skill non è autovalutabile.
          </span>
        )}
      </li>
    );
  };

  return (
    <section
      className="assessment-wizard skill-assessment-wizard"
      aria-labelledby="skill-assessment-wizard-title"
    >
      <PageHeader
        title="Valutazione skill"
        titleId="skill-assessment-wizard-title"
        subtitle="Valuta le tue skill con i form già attivi: scegli il Livello per salvare, Esperienza e Propensione sono facoltativi."
        actions={
          started ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setStarted(false)}
            >
              ← Cambia selezione
            </Button>
          ) : undefined
        }
      />

      {/* Step di scelta iniziale: tre "tastoni" (stile card della home) che
          preimpostano il filtro di ambito ed entrano nella lista. */}
      {!started && (
        <ul
          className="skill-assessment-wizard__choices"
          aria-label="Quali skill vuoi valutare?"
        >
          <li>
            <Card
              interactive
              className="skill-assessment-wizard__choice"
              role="button"
              tabIndex={myDepartmentId !== null ? 0 : -1}
              aria-disabled={myDepartmentId === null || undefined}
              aria-label="Valuta le skill della mia business unit"
              onClick={() => {
                if (myDepartmentId !== null) startWith(`${DEPT_PREFIX}${myDepartmentId}`);
              }}
              onKeyDown={(e) => {
                if (myDepartmentId === null) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  startWith(`${DEPT_PREFIX}${myDepartmentId}`);
                }
              }}
            >
              <span className="skill-assessment-wizard__choice-title">
                Le skill del mio ruolo
              </span>
              <span className="skill-assessment-wizard__choice-desc">
                {myDepartmentId === null
                  ? 'Nessuna business unit assegnata al tuo profilo.'
                  : 'Valuta le skill delle competenze della tua business unit.'}
              </span>
              {myDepartmentId !== null && (
                <span className="skill-assessment-wizard__choice-footer">
                  <span className="skill-assessment-wizard__choice-count kpi-card__value">
                    {counts.bu.rated}
                    <span className="kpi-card__value-total"> / {counts.bu.total}</span>
                  </span>
                  {myDepartmentName !== null && (
                    <span className="skill-assessment-wizard__choice-bu">
                      {myDepartmentName}
                    </span>
                  )}
                </span>
              )}
            </Card>
          </li>

          <li>
            <Card
              interactive
              className="skill-assessment-wizard__choice"
              role="button"
              tabIndex={0}
              aria-label="Valuta le skill che ti mancano"
              onClick={() => startWith('unrated')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  startWith('unrated');
                }
              }}
            >
              <span className="skill-assessment-wizard__choice-title">
                Completa le mancanti
              </span>
              <span className="skill-assessment-wizard__choice-desc">
                Le skill senza alcun voto, ancora da valutare.
              </span>
              <span className="skill-assessment-wizard__choice-count kpi-card__value">
                {counts.missing}
                <span className="kpi-card__value-total"> / {counts.all.total}</span>
              </span>
            </Card>
          </li>

          <li>
            <Card
              interactive
              className="skill-assessment-wizard__choice"
              role="button"
              tabIndex={0}
              aria-label="Valuta tutte le skill"
              onClick={() => startWith('all')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  startWith('all');
                }
              }}
            >
              <span className="skill-assessment-wizard__choice-title">
                Rivedi tutte le skill
              </span>
              <span className="skill-assessment-wizard__choice-desc">
                Scorri e valuta l'intero catalogo delle skill.
              </span>
              <span className="skill-assessment-wizard__choice-count kpi-card__value">
                {counts.all.rated}
                <span className="kpi-card__value-total"> / {counts.all.total}</span>
              </span>
            </Card>
          </li>
        </ul>
      )}

      {/* Skill suggerite: le più valutate/recensite tra quelle che l'utente non
          ha ancora valutato. Con un reparto sono quelle della propria business
          unit; senza reparto, le più popolari dell'intero catalogo. Mostrata
          nella schermata iniziale (sotto i tre tastoni), con la stessa tabella
          delle skill da valutare, per compilarle al volo. Visibile quando ci
          sono suggerimenti. */}
      {!started && suggestedRows.length > 0 && (
        <section className="skill-assessment-wizard__suggested">
          {/* Titolo e sottotitolo di sezione (fuori dalla tabella). */}
          <header className="skill-assessment-wizard__suggested-header">
            <h2 className="skill-assessment-wizard__suggested-title">
              Skill suggerite
            </h2>
            <p className="skill-assessment-wizard__suggested-subtitle">
              {myDepartmentId !== null
                ? 'Le skill più valutate dai colleghi della tua business unit, che non hai ancora valutato.'
                : 'Le skill più valutate in azienda, che non hai ancora valutato.'}
            </p>
          </header>

          <Card className="assessment-wizard__skills-card">
            <div className="assessment-wizard__skills-header">
              {/* Nell'header della tabella resta solo l'etichetta "Skill" con la
                  guida ai Livelli (la "i" informativa) e le intestazioni di
                  colonna, come nella tabella principale. */}
              <h3 className="assessment-wizard__skills-title">Skill</h3>
              <LivelloGuide />
              <div
                className="assessment-wizard__inputs assessment-wizard__col-headers"
                aria-hidden="true"
              >
                <span className="assessment-wizard__col-header skill-list__picker-level">
                  Livello
                </span>
                <span className="assessment-wizard__col-header skill-list__picker-years">
                  Esperienza
                </span>
                <span className="assessment-wizard__col-header skill-list__picker-propension">
                  Propensione
                </span>
              </div>
            </div>

            <ul className="assessment-wizard__skills">
              {suggestedRows.map((row) => renderSkillRow(row))}
            </ul>
          </Card>
        </section>
      )}

      {started && (
      <>
      <Field
        id="skill-assessment-search"
        label="Cerca skill"
        hint="Filtra per ambito e tipologia; la ricerca opera nella selezione corrente."
      >
        <div className="competence-search">
          {/* Filtro di ambito: generale + una voce per business unit e per
              professione. */}
          <span className="competence-search__department user-admin__select">
            <select
              id="skill-assessment-scope"
              aria-label="Filtra le skill per ambito"
              value={scopeFilter}
              disabled={competencesQuery.isLoading}
              onChange={(e) => {
                setScopeFilter(e.target.value);
                // Cambiando ambito la selezione visibile cambia: azzera il pin.
                setPinnedSkillIds(new Set<string>());
              }}
            >
              <optgroup label="Generale">
                <option value="mine">Le mie skill</option>
                <option value="unrated">Non valutate</option>
                <option value="all">Tutte le skill</option>
              </optgroup>
              {departmentOptions.length > 0 && (
                <optgroup label="Per business unit">
                  {departmentOptions.map((d) => (
                    <option key={d.id} value={`${DEPT_PREFIX}${d.id}`}>
                      {d.name}
                    </option>
                  ))}
                </optgroup>
              )}
              {jobOptions.length > 0 && (
                <optgroup label="Per professione">
                  {jobOptions.map((j) => (
                    <option key={j.id} value={`${JOB_PREFIX}${j.id}`}>
                      {j.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </span>

          {/* Filtro per tipologia di skill (e-CF): tutte / knowledge / skill /
              technology / senza tipo. */}
          <span className="competence-search__department user-admin__select">
            <select
              id="skill-assessment-type"
              aria-label="Filtra le skill per tipologia"
              value={typeFilter}
              disabled={skillsQuery.isLoading}
              onChange={(e) => {
                setTypeFilter(e.target.value as TypeFilter);
                // Cambiando tipologia la selezione visibile cambia: azzera il pin.
                setPinnedSkillIds(new Set<string>());
              }}
            >
              <option value="all">Tutte le tipologie</option>
              {SKILL_TYPE_VALUES.map((t) => (
                <option key={t} value={t}>
                  {SKILL_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </span>

          <TextInput
            id="skill-assessment-search"
            className="competence-search__input"
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              // La ricerca ridefinisce la selezione visibile: azzera il pin.
              setPinnedSkillIds(new Set<string>());
            }}
            placeholder="Cerca una skill…"
            autoComplete="off"
          />
        </div>
      </Field>

      {saveSelfScore.isError && (
        <Alert tone="error">Salvataggio non riuscito. Riprova.</Alert>
      )}

      <Card className="assessment-wizard__skills-card">
        <div className="assessment-wizard__skills-header">
          <h3 className="assessment-wizard__skills-title">Skills</h3>
          {/* Guida ai Livelli: una sola volta accanto al titolo, non per riga. */}
          <LivelloGuide />
          {/* Etichette di colonna allineate sopra i rispettivi select: stesso
              layout della riga (`assessment-wizard__inputs`) e stesse classi di
              larghezza dei picker, così ogni etichetta cade sopra il select. */}
          <div className="assessment-wizard__inputs assessment-wizard__col-headers" aria-hidden="true">
            <span className="assessment-wizard__col-header skill-list__picker-level">
              Livello
            </span>
            <span className="assessment-wizard__col-header skill-list__picker-years">
              Esperienza
            </span>
            <span className="assessment-wizard__col-header skill-list__picker-propension">
              Propensione
            </span>
          </div>
        </div>

        {isLoading ? (
          <p role="status">Caricamento delle skill…</p>
        ) : filteredRows.length === 0 ? (
          <p role="status">{emptyMessage}</p>
        ) : (
          <ul className="assessment-wizard__skills">
            {filteredRows.map((row) => renderSkillRow(row))}
          </ul>
        )}
      </Card>
      </>
      )}
    </section>
  );
}
